// Inline help affordances: a single reused popover (like chart.ts's hover tooltip)
// plus the factories the rest of the app calls — infoButton(key) for the ⓘ glyph,
// helpLink(key) for an inline deep-link into the Methods page, and attachHelp for
// wiring any element. Content comes from the shared registry, so a popover and the
// Methods page always say the same thing. Styling is class-only (CSS tokens), so it
// re-casts across the day/dusk/night palettes and never flashes white on a bridge.

import type { HelpKey } from "./types.js";
import { HELP } from "./content.js";

// The Methods page deep-link uses the app router; main.ts injects navigate() so we
// don't import the entry module. Fallback drives the browser directly.
let navigateTo: (path: string) => void = (path) => {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

/** Wire the help popover to the app router (call once at startup). */
export function initHelp(navigate: (path: string) => void): void {
  navigateTo = navigate;
}

let pop: HTMLElement | null = null;
let openFor: HTMLElement | null = null;
let listenersBound = false;

function ensurePop(): HTMLElement {
  if (pop !== null) return pop;
  const p = document.createElement("div");
  p.className = "help-pop";
  p.setAttribute("role", "dialog");
  p.hidden = true;
  document.body.appendChild(p);
  pop = p;
  if (!listenersBound) {
    listenersBound = true;
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closePop();
    });
    document.addEventListener("click", (e) => {
      if (openFor === null || pop === null) return;
      const t = e.target as Node;
      if (!pop.contains(t) && !openFor.contains(t)) closePop();
    });
    window.addEventListener("resize", closePop);
    window.addEventListener("scroll", closePop, true);
  }
  return p;
}

function closePop(): void {
  if (pop !== null) pop.hidden = true;
  if (openFor !== null) openFor.setAttribute("aria-expanded", "false");
  openFor = null;
}

function metaLine(e: (typeof HELP)[HelpKey]): HTMLElement | null {
  const bits: string[] = [];
  if (e.units !== undefined && e.units.length > 0) bits.push(e.units);
  if (e.source !== undefined && e.source.length > 0) bits.push(e.source);
  if (bits.length === 0) return null;
  const m = document.createElement("p");
  m.className = "help-pop-meta";
  m.textContent = bits.join(" · ");
  return m;
}

function openPop(anchor: HTMLElement, key: HelpKey): void {
  const e = HELP[key];
  if (e === undefined) return; // key not yet in the registry — fail quiet, never throw
  const p = ensurePop();
  p.replaceChildren();

  const h = document.createElement("p");
  h.className = "help-pop-term";
  h.textContent = e.term;
  p.appendChild(h);

  const s = document.createElement("p");
  s.className = "help-pop-short";
  s.textContent = e.short;
  p.appendChild(s);

  const meta = metaLine(e);
  if (meta !== null) p.appendChild(meta);

  const lim = document.createElement("p");
  lim.className = "help-pop-limit";
  lim.textContent = `Limitation — ${e.limitation}`;
  p.appendChild(lim);

  const more = document.createElement("a");
  more.className = "help-pop-more";
  more.href = `/methods#${e.anchor}`;
  more.textContent = "Full details →";
  more.addEventListener("click", (ev) => {
    ev.preventDefault();
    closePop();
    navigateTo(`/methods#${e.anchor}`);
  });
  p.appendChild(more);

  // Position: anchored under the trigger, clamped into the viewport. Fixed so it
  // rides above the map canvas regardless of scroll container.
  p.hidden = false;
  const r = anchor.getBoundingClientRect();
  const pw = p.offsetWidth;
  const ph = p.offsetHeight;
  const margin = 8;
  let left = r.left;
  if (left + pw > window.innerWidth - margin) left = window.innerWidth - margin - pw;
  if (left < margin) left = margin;
  let top = r.bottom + 6;
  if (top + ph > window.innerHeight - margin) top = r.top - 6 - ph; // flip above
  if (top < margin) top = margin;
  p.style.left = `${Math.round(left)}px`;
  p.style.top = `${Math.round(top)}px`;

  openFor = anchor;
  anchor.setAttribute("aria-expanded", "true");
}

/** Make `anchor` toggle the help popover for `key` on click/tap. */
export function attachHelp(anchor: HTMLElement, key: HelpKey): void {
  anchor.setAttribute("aria-haspopup", "dialog");
  anchor.setAttribute("aria-expanded", "false");
  anchor.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (openFor === anchor) {
      closePop();
      return;
    }
    closePop();
    openPop(anchor, key);
  });
}

/** A small circled-ⓘ button, already wired to `key`. Drop it beside a title/label. */
export function infoButton(key: HelpKey): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "help-i";
  b.textContent = "i";
  const e = HELP[key];
  b.setAttribute("aria-label", `About ${e !== undefined ? e.term : key}`);
  attachHelp(b, key);
  return b;
}

/** An inline text link that jumps to this concept's section on the Methods page. */
export function helpLink(key: HelpKey, label?: string): HTMLAnchorElement {
  const e = HELP[key];
  const a = document.createElement("a");
  a.className = "help-link";
  const href = `/methods#${e !== undefined ? e.anchor : ""}`;
  a.href = href;
  a.textContent = label ?? (e !== undefined ? e.term : key);
  a.addEventListener("click", (ev) => {
    ev.preventDefault();
    navigateTo(href);
  });
  return a;
}
